import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, FolderKanban, CheckCircle2, TrendingUp, TrendingDown, Plus, User, AlertTriangle, Clock, Filter, X, Sparkles, Lock, Crown, Users, ArrowRight, ChevronDown, ChevronUp, Shield, XCircle, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Layout from '@/components/Layout';
import AIReportDialog from '@/components/AIReportDialog';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { UpgradeButton } from '@/components/FeatureGate';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useResourceCalculations } from '@/hooks/useResourceCalculations';

// Custom Tooltip for recharts that respects dark mode
const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // Check if dark mode is enabled
    const isDark = document.documentElement.classList.contains('dark');
    
    return (
      <div 
        style={{
          backgroundColor: isDark ? '#1e293b' : '#ffffff',
          border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          borderRadius: '0.5rem',
          padding: '0.75rem',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
        }}
      >
        <p style={{ 
          fontWeight: 600, 
          color: isDark ? '#f1f5f9' : '#0f172a',
          marginBottom: '0.5rem'
        }}>
          {label}
        </p>
        {payload.map((entry, index) => (
          <p key={index} style={{ 
            fontSize: '0.875rem', 
            color: isDark ? '#94a3b8' : '#475569',
            margin: '0.25rem 0'
          }}>
            <span style={{ color: entry.color }}>●</span>{' '}
            {entry.name}: <span style={{ 
              fontWeight: 500, 
              color: isDark ? '#f1f5f9' : '#0f172a' 
            }}>
              CHF {entry.value?.toLocaleString('de-CH')}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Helper function to format time as h:m:s
const formatTimeHMS = (totalSeconds) => {
  if (!totalSeconds || totalSeconds === 0) return '0s';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.round(totalSeconds % 60);
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  
  return parts.join(' ');
};

// Helper function to format hours (decimal) as h/m/s
const formatHoursToHMS = (hours) => {
  if (!hours || hours === 0) return '0s';
  const totalSeconds = Math.round(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  
  return parts.join(' ');
};

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { canUseAIFeatures } = useSubscription();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [criticalPaths, setCriticalPaths] = useState({}); // project_id -> critical_path_data
  const [timeSummaries, setTimeSummaries] = useState({}); // project_id -> time_summary
  const [allUsers, setAllUsers] = useState([]);
  const [aiReportOpen, setAiReportOpen] = useState(false);
  
  // Resource Planning states
  const [timelineData, setTimelineData] = useState(null);
  const [planningSettings, setPlanningSettings] = useState(null);
  const [resourceDataLoading, setResourceDataLoading] = useState(true);
  
  // Resource time period selection
  const [resourcePeriod, setResourcePeriod] = useState('month'); // 'week', 'month', 'quarter', 'year'
  
  // Calculate date range based on selected period
  const resourceDateRange = useMemo(() => {
    const now = new Date();
    let start, end;
    
    switch (resourcePeriod) {
      case 'week':
        // Current week (Monday to Sunday)
        const dayOfWeek = now.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        start = new Date(now);
        start.setDate(now.getDate() + mondayOffset);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'quarter':
        // Current quarter
        const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
        start = new Date(now.getFullYear(), quarterMonth, 1);
        end = new Date(now.getFullYear(), quarterMonth + 3, 0);
        break;
      case 'year':
        // Current year
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      case 'month':
      default:
        // Current month
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
    }
    
    // Use local date formatting to avoid timezone issues
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      start: formatLocalDate(start),
      end: formatLocalDate(end)
    };
  }, [resourcePeriod]);
  
  // Get period label for display
  const getPeriodLabel = () => {
    const now = new Date();
    switch (resourcePeriod) {
      case 'week':
        const startDate = new Date(resourceDateRange.start);
        const endDate = new Date(resourceDateRange.end);
        return `KW ${getISOWeek(startDate)} (${startDate.toLocaleDateString('de-CH', { day: '2-digit', month: 'short' })} - ${endDate.toLocaleDateString('de-CH', { day: '2-digit', month: 'short' })})`;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3) + 1;
        return `Q${quarter} ${now.getFullYear()}`;
      case 'year':
        return `Jahr ${now.getFullYear()}`;
      case 'month':
      default:
        return now.toLocaleDateString('de-CH', { month: 'long', year: 'numeric' });
    }
  };
  
  // Helper to get ISO week number
  const getISOWeek = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };
  
  // Use the same hook as ResourcePlanning.js for consistent calculations
  const {
    filteredSummaryKPI,
    filteredHoursKPI,
    calculatedEmployeeUtilization
  } = useResourceCalculations({
    dateRange: resourceDateRange,
    timelineData,
    planningSettings,
    filterEmployee: '',
    filterProject: ''
  });
  
  // State for expandable top employees section
  const [showTopEmployees, setShowTopEmployees] = useState(false);
  
  // Risk dashboard state
  const [riskSummary, setRiskSummary] = useState(null);
  const [riskLoading, setRiskLoading] = useState(true);
  const [showTopRisks, setShowTopRisks] = useState(false);
  
  // Get top 5 busiest employees
  const topBusyEmployees = useMemo(() => {
    if (!calculatedEmployeeUtilization || calculatedEmployeeUtilization.length === 0) {
      return [];
    }
    return [...calculatedEmployeeUtilization]
      .sort((a, b) => b.utilization_percent - a.utilization_percent)
      .slice(0, 5);
  }, [calculatedEmployeeUtilization]);
  
  // Filter states
  const [filterTeamMember, setFilterTeamMember] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchStats();
    fetchAllUsers();
    fetchRiskSummary();
  }, []);
  
  // Fetch risk summary for dashboard widget
  const fetchRiskSummary = async () => {
    setRiskLoading(true);
    try {
      const response = await axios.get(`${API}/risk-management/dashboard-summary`);
      setRiskSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch risk summary', error);
    } finally {
      setRiskLoading(false);
    }
  };
  
  // Fetch resource data when period changes
  useEffect(() => {
    fetchResourceData();
  }, [resourceDateRange]);
  
  // Fetch resource planning data
  const fetchResourceData = async () => {
    setResourceDataLoading(true);
    try {
      const [timelineRes, settingsRes] = await Promise.all([
        axios.get(`${API}/resource-planning/timeline`, {
          params: {
            start_date: resourceDateRange.start,
            end_date: resourceDateRange.end
          }
        }),
        axios.get(`${API}/resource-planning/planning-settings`)
      ]);
      
      // IMPORTANT: Set settings FIRST, then timeline data
      // This ensures calculations use correct settings
      if (settingsRes.data) {
        setPlanningSettings(settingsRes.data);
      }
      setTimelineData(timelineRes.data);
    } catch (error) {
      console.error('Failed to fetch resource data', error);
    } finally {
      setResourceDataLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  useEffect(() => {
    // Fetch critical paths and time summaries for all projects after stats are loaded
    if (stats?.recent_projects) {
      fetchCriticalPaths(stats.recent_projects);
      fetchTimeSummaries(stats.recent_projects);
    }
  }, [stats]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSummaries = async (projects) => {
    const summaries = {};
    for (const project of projects) {
      try {
        const response = await axios.get(`${API}/projects/${project.id}/time-summary`);
        summaries[project.id] = response.data;
      } catch (error) {
        console.error(`Failed to fetch time summary for project ${project.id}`, error);
      }
    }
    setTimeSummaries(summaries);
  };

  const fetchCriticalPaths = async (projects) => {
    const paths = {};
    for (const project of projects) {
      if (project.status === 'active') {
        try {
          const response = await axios.get(`${API}/projects/${project.id}/critical-path`);
          paths[project.id] = response.data;
        } catch (error) {
          console.error(`Failed to fetch critical path for project ${project.id}`, error);
        }
      }
    }
    setCriticalPaths(paths);
  };

  // Filter projects based on selected filters - MUST be defined before chartData and getGanttData
  const getFilteredProjects = () => {
    if (!stats?.recent_projects) return [];
    
    return stats.recent_projects.filter(project => {
      // Filter by status
      if (filterStatus !== 'all' && project.status !== filterStatus) {
        return false;
      }
      
      // Filter by team member
      if (filterTeamMember !== 'all') {
        const isOwner = project.owner_id === filterTeamMember;
        const isTeamMember = project.team_member_ids?.includes(filterTeamMember);
        if (!isOwner && !isTeamMember) {
          return false;
        }
      }
      
      return true;
    });
  };

  const filteredProjects = getFilteredProjects();
  const hasActiveFilters = filterTeamMember !== 'all' || filterStatus !== 'all';

  // Chart data based on filtered projects
  const chartData = filteredProjects.map(p => ({
    name: p.name.substring(0, 15) + (p.name.length > 15 ? '...' : ''),
    Geplant: p.planned_cost,
    Effektiv: p.actual_cost
  }));

  const getGanttData = () => {
    // Use filtered projects if filters are active, otherwise use all recent projects
    const projectsToUse = hasActiveFilters ? filteredProjects : (stats?.recent_projects || []);
    const activeProjects = projectsToUse.filter(p => p.status === 'active');
    
    return activeProjects.map(project => {
      const start = new Date(project.start_date);
      const end = new Date(project.end_date);
      const today = new Date();
      
      const totalDuration = end.getTime() - start.getTime();
      const elapsed = today.getTime() - start.getTime();
      const progress = Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);
      
      return {
        ...project,
        startDate: start,
        endDate: end,
        progress: Math.round(progress),
        daysTotal: Math.ceil(totalDuration / (1000 * 60 * 60 * 24)),
        daysElapsed: Math.ceil(elapsed / (1000 * 60 * 60 * 24))
      };
    }).sort((a, b) => a.startDate - b.startDate);
  };

  const ganttProjects = getGanttData();

  const clearFilters = () => {
    setFilterTeamMember('all');
    setFilterStatus('all');
  };

  // Calculate filtered statistics
  const filteredStats = {
    total_projects: filteredProjects.length,
    active_projects: filteredProjects.filter(p => p.status === 'active').length,
    completed_projects: filteredProjects.filter(p => p.status === 'completed').length,
    total_tasks: filteredProjects.reduce((sum, p) => sum + (p.task_count || 0), 0),
    completed_tasks: filteredProjects.reduce((sum, p) => sum + (p.completed_tasks || 0), 0),
    total_planned_cost: filteredProjects.reduce((sum, p) => sum + (p.planned_cost || 0), 0),
    total_actual_cost: filteredProjects.reduce((sum, p) => sum + (p.actual_cost || 0), 0)
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-xl font-medium">Laden...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-muted-foreground mt-2">Willkommen zurück, {user?.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <UpgradeButton
              feature="ai_features"
              variant="outline"
              onClick={() => setAiReportOpen(true)}
              data-testid="ai-report-button"
              className="border-purple-200 text-purple-600 hover:bg-purple-50 hover:text-purple-700 dark:border-purple-800 dark:hover:bg-purple-950/30"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              KI-Report
            </UpgradeButton>
            <Button onClick={() => navigate('/projects')} data-testid="goto-projects-button">
              <Plus className="w-4 h-4 mr-2" />
              Neues Projekt
            </Button>
          </div>
        </div>

        {/* Filter Section */}
        <Card className="bg-muted/30" data-testid="filter-section">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filter:</span>
              </div>
              
              {/* Team Member Filter */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Teammitglied:</span>
                <Select value={filterTeamMember} onValueChange={setFilterTeamMember}>
                  <SelectTrigger className="w-[180px] h-8 text-sm" data-testid="filter-team-member">
                    <SelectValue placeholder="Alle Mitglieder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Mitglieder</SelectItem>
                    {allUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Status:</span>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="filter-status">
                    <SelectValue placeholder="Alle Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="completed">Abgeschlossen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  className="h-8 text-xs"
                  data-testid="clear-filters-button"
                >
                  <X className="w-3 h-3 mr-1" />
                  Filter zurücksetzen
                </Button>
              )}
              
              {/* Filter Results Count */}
              {hasActiveFilters && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {filteredProjects.length} von {stats?.recent_projects?.length || 0} Projekten
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card data-testid="stat-card-projects">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {hasActiveFilters ? 'Gefilterte Projekte' : 'Gesamt Projekte'}
              </CardTitle>
              <FolderKanban className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {filteredStats.total_projects}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {filteredStats.active_projects} aktiv
              </p>
              {filteredProjects.length > 0 && (
                <div className="flex items-center gap-3 mt-2 pt-2 border-t">
                  {(() => {
                    const onTrack = filteredProjects.filter(p => {
                      const critical = criticalPaths[p.id]?.critical_path_task_ids?.length || 0;
                      const overdue = criticalPaths[p.id]?.overdue_task_ids?.length || 0;
                      return critical === 0 && overdue === 0;
                    }).length;
                    const withIssues = filteredProjects.length - onTrack;
                    return (
                      <>
                        <span className="flex items-center gap-1 text-xs">
                          <span className="w-2 h-2 bg-green-500 rounded-full" />
                          {onTrack} ok
                        </span>
                        {withIssues > 0 && (
                          <span className="flex items-center gap-1 text-xs text-red-600">
                            <span className="w-2 h-2 bg-red-500 rounded-full" />
                            {withIssues} verzögert
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="stat-card-tasks">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {hasActiveFilters ? 'Gefilterte Tasks' : 'Gesamt Tasks'}
              </CardTitle>
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {filteredStats.total_tasks}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {filteredStats.completed_tasks} abgeschlossen
              </p>
              {filteredProjects.length > 0 && Object.keys(criticalPaths).length > 0 && (
                <div className="flex items-center gap-3 mt-2 pt-2 border-t">
                  {(() => {
                    let totalOverdue = 0;
                    let totalCritical = 0;
                    filteredProjects.forEach(p => {
                      const cp = criticalPaths[p.id];
                      if (cp) {
                        totalOverdue += cp?.overdue_task_ids?.length || 0;
                        totalCritical += cp?.critical_path_task_ids?.length || 0;
                      }
                    });
                    return (
                      <>
                        {totalOverdue > 0 && (
                          <span className="flex items-center gap-1 text-xs text-red-600">
                            <span className="w-2 h-2 bg-red-500 rounded-full" />
                            {totalOverdue} überfällig
                          </span>
                        )}
                        {totalCritical > 0 && (
                          <span className="flex items-center gap-1 text-xs text-orange-600">
                            <span className="w-2 h-2 bg-orange-500 rounded-full" />
                            {totalCritical} kritisch
                          </span>
                        )}
                        {totalOverdue === 0 && totalCritical === 0 && (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <span className="w-2 h-2 bg-green-500 rounded-full" />
                            alle ok
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="stat-card-planned">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {hasActiveFilters ? 'Gefilterte Kosten (Geplant)' : 'Geplante Kosten'}
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                CHF {filteredStats.total_planned_cost?.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-card-actual">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {hasActiveFilters ? 'Gefilterte Kosten (Effektiv)' : 'Effektive Kosten'}
              </CardTitle>
              {(filteredStats.total_actual_cost - filteredStats.total_planned_cost) >= 0 ? (
                <TrendingUp className="h-4 w-4 text-destructive" />
              ) : (
                <TrendingDown className="h-4 w-4 text-green-600" />
              )}
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                CHF {filteredStats.total_actual_cost?.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
              </div>
              <p className={`text-xs mt-1 ${
                (filteredStats.total_actual_cost - filteredStats.total_planned_cost) > 0 ? 'text-destructive' : 'text-green-600'
              }`}>
                {(filteredStats.total_actual_cost - filteredStats.total_planned_cost) >= 0 ? '+' : ''}
                CHF {Math.abs(filteredStats.total_actual_cost - filteredStats.total_planned_cost).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Resource Utilization Widget */}
        {timelineData && planningSettings && !resourceDataLoading && (
          <Card data-testid="resource-utilization-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Team-Auslastung
                </CardTitle>
                <CardDescription>
                  {getPeriodLabel()}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select value={resourcePeriod} onValueChange={setResourcePeriod}>
                  <SelectTrigger className="w-[130px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Diese Woche</SelectItem>
                    <SelectItem value="month">Dieser Monat</SelectItem>
                    <SelectItem value="quarter">Dieses Quartal</SelectItem>
                    <SelectItem value="year">Dieses Jahr</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/resource-planning')}
                  className="gap-1"
                >
                  Details
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Total Employees */}
                <div className="text-center p-3 rounded-lg bg-muted/50">
                  <p className="text-2xl font-bold">{filteredSummaryKPI.total}</p>
                  <p className="text-xs text-muted-foreground">Mitarbeiter</p>
                </div>
                
                {/* Overbooked */}
                <div className={`text-center p-3 rounded-lg ${filteredSummaryKPI.overbooked > 0 ? 'bg-red-100 dark:bg-red-900/30' : 'bg-muted/50'}`}>
                  <p className={`text-2xl font-bold ${filteredSummaryKPI.overbooked > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                    {filteredSummaryKPI.overbooked}
                  </p>
                  <p className="text-xs text-muted-foreground">Überbucht</p>
                </div>
                
                {/* High Utilization */}
                <div className={`text-center p-3 rounded-lg ${filteredSummaryKPI.high > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-muted/50'}`}>
                  <p className={`text-2xl font-bold ${filteredSummaryKPI.high > 0 ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                    {filteredSummaryKPI.high}
                  </p>
                  <p className="text-xs text-muted-foreground">Hohe Auslastung</p>
                </div>
                
                {/* Available */}
                <div className="text-center p-3 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {filteredSummaryKPI.normal + filteredSummaryKPI.low}
                  </p>
                  <p className="text-xs text-muted-foreground">Verfügbar</p>
                </div>
              </div>
              
              {/* Utilization Progress Bar */}
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Planungsgrad Projekt</span>
                  <span className={`font-medium ${
                    filteredHoursKPI.utilizationPercent > 100 ? 'text-red-600' :
                    filteredHoursKPI.utilizationPercent > 80 ? 'text-amber-600' :
                    'text-green-600'
                  }`}>
                    {filteredHoursKPI.utilizationPercent}%
                  </span>
                </div>
                <Progress 
                  value={Math.min(filteredHoursKPI.utilizationPercent, 100)} 
                  className="h-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{filteredHoursKPI.totalProjectHours}h geplant</span>
                  <span>{filteredHoursKPI.totalAvailableProjectHours}h verfügbar</span>
                </div>
              </div>
              
              {/* Expandable Top 5 Employees Section */}
              {topBusyEmployees.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => setShowTopEmployees(!showTopEmployees)}
                    className="flex items-center justify-between w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="font-medium">Top 5 ausgelastete Mitarbeiter</span>
                    {showTopEmployees ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  
                  {showTopEmployees && (
                    <div className="mt-3 space-y-2">
                      {topBusyEmployees.map((employee, index) => (
                        <div 
                          key={employee.user_id}
                          className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-xs font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{employee.user_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {employee.allocated_hours}h / {employee.total_available_project_hours}h
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Progress 
                              value={Math.min(employee.utilization_percent, 100)} 
                              className="w-16 h-1.5"
                            />
                            <span className={`text-sm font-bold min-w-[45px] text-right ${
                              employee.utilization_percent > 100 ? 'text-red-600 dark:text-red-400' :
                              employee.utilization_percent > 80 ? 'text-amber-600 dark:text-amber-400' :
                              'text-green-600 dark:text-green-400'
                            }`}>
                              {employee.utilization_percent}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Risk Dashboard Widget */}
        {riskSummary && (riskSummary.critical_count > 0 || riskSummary.high_count > 0 || riskSummary.total_risks > 0) && (
          <Card data-testid="risk-dashboard-widget">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Risiko-Übersicht
                </CardTitle>
                <CardDescription>
                  {riskSummary.projects_with_risks} Projekt{riskSummary.projects_with_risks !== 1 ? 'e' : ''} mit aktiven Risiken
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/risk-management')}
                className="text-xs"
              >
                Alle anzeigen
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="text-center p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{riskSummary.critical_count}</div>
                  <div className="text-xs text-red-600/80 dark:text-red-400/80">Kritisch</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{riskSummary.high_count}</div>
                  <div className="text-xs text-orange-600/80 dark:text-orange-400/80">Hoch</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{riskSummary.overdue_count}</div>
                  <div className="text-xs text-yellow-600/80 dark:text-yellow-400/80">Überfällig</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{riskSummary.total_risks}</div>
                  <div className="text-xs text-blue-600/80 dark:text-blue-400/80">Gesamt</div>
                </div>
              </div>
              
              {/* Top Risks - Collapsible */}
              {riskSummary.top_risks && riskSummary.top_risks.length > 0 && (
                <div className="pt-4 border-t">
                  <button
                    onClick={() => setShowTopRisks(!showTopRisks)}
                    className="flex items-center justify-between w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="font-medium">Top 5 Risiken</span>
                    {showTopRisks ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  
                  {showTopRisks && (
                    <div className="mt-3 space-y-2">
                      {riskSummary.top_risks.map((risk, index) => (
                        <div 
                          key={risk.id}
                          className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/projects/${risk.project_id}?tab=risks`)}
                        >
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                            risk.risk_level === 'critical' 
                              ? 'bg-red-100 dark:bg-red-900/50' 
                              : 'bg-orange-100 dark:bg-orange-900/50'
                          }`}>
                            {risk.risk_level === 'critical' ? (
                              <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{risk.title}</p>
                            <p className="text-xs text-muted-foreground truncate">{risk.project_name}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                risk.risk_level === 'critical' 
                                  ? 'border-red-300 text-red-600 dark:border-red-700 dark:text-red-400' 
                                  : 'border-orange-300 text-orange-600 dark:border-orange-700 dark:text-orange-400'
                              }`}
                            >
                              Score: {risk.risk_score}
                            </Badge>
                            {risk.due_date && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(risk.due_date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* No critical risks message */}
              {riskSummary.top_risks && riskSummary.top_risks.length === 0 && riskSummary.total_risks > 0 && (
                <div className="text-center py-4 text-muted-foreground">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm">Keine kritischen oder hohen Risiken</p>
                  <p className="text-xs">{riskSummary.total_risks} Risiko(en) mit niedrigerer Priorität</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {ganttProjects.length > 0 && (
          <Card data-testid="gantt-chart-card">
            <CardHeader>
              <CardTitle>Aktive Projekte - Timeline Übersicht</CardTitle>
              <CardDescription>Gantt-Chart aller laufenden Projekte</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {ganttProjects.map((project) => {
                  const criticalCount = criticalPaths[project.id]?.critical_path_task_ids?.length || 0;
                  const overdueCount = criticalPaths[project.id]?.overdue_task_ids?.length || 0;
                  const hasIssues = criticalCount > 0 || overdueCount > 0;
                  
                  return (
                    <div key={project.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-sm">{project.name}</h4>
                            {overdueCount > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/30">
                                <AlertTriangle className="w-3 h-3" />
                                {overdueCount} überfällig
                              </span>
                            )}
                            {criticalCount > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-500/30">
                                <AlertTriangle className="w-3 h-3" />
                                {criticalCount} kritisch
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              ({project.startDate.toLocaleDateString('de-CH', { day: '2-digit', month: 'short' })} - 
                              {project.endDate.toLocaleDateString('de-CH', { day: '2-digit', month: 'short', year: 'numeric' })})
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                            <span className="flex items-center">
                              <User className="w-3 h-3 mr-1" />
                              {project.owner_name}
                            </span>
                            <span>CHF {project.actual_cost.toLocaleString('de-CH')} / {project.planned_cost.toLocaleString('de-CH')}</span>
                          </div>
                        </div>
                        <div className="text-xs font-medium" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {project.progress}%
                        </div>
                      </div>
                      <div className={`relative h-8 rounded-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${hasIssues ? 'bg-red-500/20 dark:bg-red-500/30 ring-2 ring-red-500/50' : 'bg-muted'}`}
                           onClick={() => navigate(`/projects/${project.id}`)}>
                        <div 
                          className={`absolute h-full rounded-sm transition-all ${hasIssues ? 'bg-red-500/80' : 'bg-primary/80'}`}
                          style={{ width: `${project.progress}%` }}
                        />
                        <div className="absolute inset-0 flex items-center px-2 text-xs text-foreground">
                          {project.daysElapsed} / {project.daysTotal} Tage
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {chartData.length > 0 && (
          <Card className="col-span-1 md:col-span-3" data-testid="chart-card">
            <CardHeader>
              <CardTitle>Projekt Kostenübersicht</CardTitle>
              <CardDescription>Geplante vs. Effektive Kosten der letzten Projekte</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    content={<CustomChartTooltip />}
                    wrapperStyle={{ outline: 'none' }}
                    cursor={{ fill: 'rgba(100, 100, 100, 0.1)' }}
                  />
                  <Legend />
                  <Bar dataKey="Geplant" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Effektiv" fill="hsl(var(--secondary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {filteredProjects.length > 0 && (
          <Card data-testid="recent-projects-card">
            <CardHeader>
              <CardTitle>
                {hasActiveFilters ? 'Gefilterte Projekte' : 'Aktuelle Projekte'}
              </CardTitle>
              <CardDescription>
                {hasActiveFilters 
                  ? `${filteredProjects.length} Projekt${filteredProjects.length !== 1 ? 'e' : ''} gefunden`
                  : 'Ihre neuesten Projekte'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredProjects.map((project) => {
                  const criticalCount = criticalPaths[project.id]?.critical_path_task_ids?.length || 0;
                  const overdueCount = criticalPaths[project.id]?.overdue_task_ids?.length || 0;
                  const hasIssues = criticalCount > 0 || overdueCount > 0;
                  const timeSummary = timeSummaries[project.id];
                  
                  return (
                    <div
                      key={project.id}
                      className={`flex items-center justify-between p-4 border rounded-sm hover:shadow-md transition-shadow cursor-pointer ${
                        hasIssues ? 'border-red-500/50 bg-red-500/10 dark:bg-red-500/20' : 'border-border'
                      }`}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      data-testid={`project-item-${project.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-foreground">{project.name}</h3>
                          {overdueCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-600 dark:text-red-400">
                              <AlertTriangle className="w-3 h-3" />
                              {overdueCount} überfällig
                            </span>
                          )}
                          {criticalCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-500/20 text-orange-600 dark:text-orange-400">
                              <AlertTriangle className="w-3 h-3" />
                              {criticalCount} kritisch
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{project.description}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                          <span>
                            <User className="w-3 h-3 inline mr-1" />
                            {project.owner_name}
                          </span>
                          {timeSummary && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatHoursToHMS(timeSummary.total_hours)} erfasst
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          CHF {project.actual_cost.toLocaleString('de-CH', { minimumFractionDigits: 2 })} / CHF {project.planned_cost.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          project.status === 'active' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                        }`}>
                          {project.status === 'active' ? 'Aktiv' : 'Abgeschlossen'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Time Overview per Project */}
        {filteredProjects.length > 0 && Object.keys(timeSummaries).length > 0 && (
          <Card data-testid="time-overview-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Zeitübersicht
              </CardTitle>
              <CardDescription>Erfasste Stunden pro Projekt und Mitarbeiter</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredProjects.map((project) => {
                  const timeSummary = timeSummaries[project.id];
                  // Show projects with any tracked time (even seconds)
                  if (!timeSummary || (timeSummary.total_seconds === 0 && timeSummary.total_minutes === 0)) return null;
                  
                  // Filter by team member if set
                  let displayedUsers = timeSummary.by_user || [];
                  if (filterTeamMember !== 'all') {
                    displayedUsers = displayedUsers.filter(u => u.user_id === filterTeamMember);
                  }
                  
                  if (displayedUsers.length === 0) return null;
                  
                  // Calculate max seconds for progress bar scaling
                  const maxSeconds = Math.max(...displayedUsers.map(u => u.seconds || u.minutes * 60), 1);
                  
                  return (
                    <div key={project.id} className="p-4 bg-muted/30 rounded-lg space-y-3">
                      {/* Project Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold">{project.name}</div>
                          <div className="text-xs text-muted-foreground">
                            Stundensatz: CHF {timeSummary.hourly_rate}/h
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {formatTimeHMS(timeSummary.total_seconds || timeSummary.total_minutes * 60)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            = CHF {timeSummary.total_cost.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                      
                      {/* Team Members */}
                      {displayedUsers.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-border/50">
                          {displayedUsers.map((member, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              {/* Avatar */}
                              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium text-primary">
                                {member.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                              </div>
                              
                              {/* Name and Progress */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium truncate">{member.name}</span>
                                  <span className="text-sm font-mono font-bold ml-2">{formatTimeHMS(member.seconds || member.minutes * 60)}</span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-primary rounded-full transition-all"
                                    style={{ width: `${((member.seconds || member.minutes * 60) / maxSeconds) * 100}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Total */}
                <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg border-2 border-primary/30">
                  <div className="font-semibold text-lg">Gesamt alle Projekte</div>
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatTimeHMS(Object.values(timeSummaries).reduce((sum, ts) => sum + (ts?.total_seconds || (ts?.total_minutes || 0) * 60), 0))}
                    </div>
                    <div className="text-sm font-medium">
                      CHF {Object.values(timeSummaries).reduce((sum, ts) => sum + (ts?.total_cost || 0), 0).toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Report Dialog */}
        <AIReportDialog
          open={aiReportOpen}
          onOpenChange={setAiReportOpen}
          projects={stats?.recent_projects || []}
        />
      </div>
    </Layout>
  );
};

export default Dashboard;
